import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { formatCount, moreRowsText, NextRows } from './grid-footer';
import {
  ClientSideRowModelApiModule,
  DefaultMenuItem,
  ExternalFilterModule,
  IServerSideDatasource,
  MenuItemDef,
  QuickFilterModule,
  RowApiModule,
  RowSelectedEvent,
  RowStyleModule,
  ScrollApiModule,
  EventApiModule,
  RowModelType,
  RowAutoHeightModule
} from 'ag-grid-community';
import {
  CellStyleModule,
  CellSelectionModule,
  ClientSideRowModelModule,
  ColDef,
  ColGroupDef,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ContextMenuModule,
  CsvExportModule,
  DateFilterModule,
  ExcelExportModule,
  GetContextMenuItems,
  GetMainMenuItems,
  GetMainMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  iconOverrides,
  IDetailCellRendererParams,
  LicenseManager,
  ManagedGridOptionKey,
  MasterDetailModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  PinnedRowModule,
  RowGroupingModule,
  RowSelectionModule,
  RowSelectionOptions,
  ServerSideRowModelModule,
  SetFilterModule,
  StatusBarModule,
  TextFilterModule,
  themeQuartz,
  TooltipModule,
  ValidationModule
} from 'ag-grid-enterprise';
import _ from 'lodash';
import { sortingAscIcon, sortingDescIcon } from './grid-icons';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  ServerSideRowModelModule,
  RowGroupingModule,
  RowSelectionModule,
  StatusBarModule,
  CellStyleModule,
  MasterDetailModule,
  PaginationModule,
  ColumnAutoSizeModule,
  TextFilterModule,
  NumberFilterModule,
  ValidationModule,
  ColumnMenuModule,
  DateFilterModule,
  SetFilterModule,
  PinnedRowModule,
  ContextMenuModule,
  ExcelExportModule,
  CsvExportModule,
  RowApiModule,
  QuickFilterModule,
  ExternalFilterModule,
  ScrollApiModule,
  RowStyleModule,
  EventApiModule,
  TooltipModule,
  CellSelectionModule,
  RowAutoHeightModule
]);
@Component({
  selector: 'gains-grid',
  standalone: false,
  templateUrl: './gains-grid.component.html',
  styleUrl: './gains-grid.component.scss'
})
export class GainsGridComponent implements OnInit {
  @Input() colDefs!: (ColDef | ColGroupDef)[];
  @Input() rowData!: any[];
  @Input() rowSelect = false;
  @Input() multiSelect = false;
  @Input() rowCount = true;
  @Input() gridOptions: GridOptions = {};
  @Input() totalRow = false;
  @Input() serverSideDatasource?: IServerSideDatasource;
  @Input() customMenus: MenuItemDef[] = [];
  @Input() detailRowHeight?: number;
  @Input() detailRowAutoHeight = true;
  @Output() selectedRows = new EventEmitter<any[]>();
  @Input() isExternalFilterPresent?: () => boolean;
  @Input() doesExternalFilterPass?: (node: any) => boolean;
  @Input() shouldHideFilterMenu = false;
  @Input() masterDetail = false;
  @Input() rowModelType = 'clientSide';
  @Input() context: any;
  @Output() cellClicked = new EventEmitter<any>();
  @Input() detailCellRenderer: any;

  /** The footer under the grid counts the rows: `12 clients`, `3 of 12 clients` when a search or a filter hides some. */
  @Input() showCount = true;
  /** The rows come page by page: the footer says how many are loaded, and offers the next page. */
  @Input() paged = false;
  /** A page loaded by a cursor: more rows exist than are loaded, and Load more, under the grid, asks for them. */
  @Input() hasMore = false;
  /** Whether the next page is on its way: Load more shows it, and does not ask twice. */
  @Input() loadingMore = false;
  /** How many rows there are, when the page gives the grid fewer of them (its search narrows them); the rows given otherwise. */
  @Input() loadedCount?: number;
  /** What the rows are, in the footer: `Showing all 12 clients`. */
  @Input() rowsLabel = 'rows';
  /** What one row is: `Showing 1 client`. */
  @Input() rowLabel = 'row';
  /** Which rows the next page holds, for the footer to say: `Newer tasks not loaded yet`, `Older alerts not loaded yet`. */
  @Input() nextRows?: NextRows;
  @Output() loadMore = new EventEmitter<void>();

  gridApi?: GridApi;
  private sortBy = '';
  private filterBy = '';
  pinnedBottomRowData: any[] = [];
  rowCountDisplay = 0;

  /** The rows the grid shows once its own filters are applied; null until it has counted them. */
  shownRows: number | null = null;

  /** The rows loaded, those a search hides included. */
  get loadedRows(): number {
    return this.loadedCount ?? this.rowData?.length ?? 0;
  }

  /** Whether the footer shows under the grid: the grid then squares its bottom corners onto it. */
  get hasFooter(): boolean {
    return this.hasMore || (this.showCount && this.loadedRows > 0);
  }

  /** The rows the grid shows, never more than those loaded. */
  get shownCount(): number {
    return Math.min(this.shownRows ?? this.rowData?.length ?? 0, this.loadedRows);
  }

  /** Whether a search or a filter hides some of the rows loaded. */
  get isNarrowed(): boolean {
    return this.shownCount < this.loadedRows;
  }

  /** `Showing 3 of 12 clients`, `Showing all 12 clients`, `Showing 2,000 tasks loaded`: the same words on every page. */
  get countParts(): { shown: string | null; all: boolean; total: string; label: string; loaded: boolean } {
    const total = this.loadedRows;
    return {
      shown: this.isNarrowed ? formatCount(this.shownCount) : null,
      all: !this.isNarrowed && total > 1 && !this.hasMore,
      total: formatCount(total),
      label: total === 1 ? this.rowLabel : this.rowsLabel,
      loaded: this.hasMore
    };
  }

  /** What follows the count: why fewer are shown, and that the last page is in. */
  get footerNotes(): string[] {
    const notes: string[] = [];
    if (this.isNarrowed) {
      notes.push('Narrowed by the search or the filters');
    }
    if (this.paged && !this.hasMore) {
      notes.push(`Every ${this.rowLabel} is loaded`);
    }
    return notes;
  }

  /** Which rows Load more brings, said next to it: `Newer tasks not loaded yet`. */
  get moreText(): string {
    return moreRowsText(this.rowsLabel, this.nextRows);
  }

  /** The rows left once the column filters and the external filter are applied, detail rows aside. */
  countShownRows(): void {
    // A grid being destroyed updates its model one last time, its filters already gone
    if (!this.gridApi || this.gridApi.isDestroyed() || this.rowModelType !== 'clientSide') {
      return;
    }
    let shown = 0;
    this.gridApi.forEachNodeAfterFilter(node => {
      if (node.data && !node.group && !node.detail) {
        shown++;
      }
    });
    this.shownRows = shown;
  }

  requestMore(): void {
    if (!this.loadingMore) {
      this.loadMore.emit();
    }
  }

  customContextMenu: GetContextMenuItems<any> | undefined = () => {
    const result: (DefaultMenuItem | MenuItemDef)[] | GetContextMenuItems<any, any> = [];
    result.push(...this.customMenus);
    if (this.customMenus.length) {
      result.push({ name: 'separator', disabled: true });
    }
    result.push('export');
    return result;
  };

  defaultNestedGridOptions = {
    getDetailRowData: (params: any) => {
      params.successCallback(params.data.children);
    },
    detailGridOptions: {
      autoSizeStrategy: {
        type: 'fitCellContents'
      },
      defaultColDef: {
        headerClass: 'nested-grid'
      },
      detailRowAutoHeight: true
    }
  } as IDetailCellRendererParams;
  @Input() detailCellRendererParams!: IDetailCellRendererParams;
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() rowSelected = new EventEmitter<RowSelectedEvent<any>>();
  @Input() selectionColumnDef: any;
  rowSelection!: RowSelectionOptions;
  @Output() radioButtonClick = new EventEmitter<any>();
  @Input() useRadioButton = false;
  /**
   * Forces custom row selection options over default.
   */
  @Input() customRowSelection?: RowSelectionOptions;
  mySvgIcons = iconOverrides({
    type: 'image',
    mask: false,
    icons: {
      asc: { svg: sortingAscIcon },
      desc: {
        svg: sortingDescIcon
      }
    }
  });
  myTheme = themeQuartz
    .withParams({
      accentColor: '#286671',
      borderColor: '#EFF2F5',
      browserColorScheme: 'light',
      cellTextColor: '#29333D',
      fontFamily: 'inherit',
      columnBorder: true,
      headerColumnBorder: true,
      headerBackgroundColor: '#FFFFFF',
      headerFontSize: 14,
      headerFontWeight: 600,
      headerTextColor: '#52667A'
    })
    .withPart(this.mySvgIcons);

  constructor() {
    const license =
      'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-059467}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{GAINSYSTEMS,_LLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{GAINS_Planning}_only_for_{3}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{GAINS_Planning}_need_to_be_licensed___{GAINS_Planning}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{21_April_2025}____[v3]_[0102]_MTc0NTE5MDAwMDAwMA==eafe01221aaf146e51acc97a5dfe2f91';
    LicenseManager.setLicenseKey(license);
  }
  ngOnInit(): void {
    this.setRadioButton();
    this.setRowSelection();
    this.setGridOptions();
  }
  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
    this.gridReady.emit(event);
    this.updateRowCount();

    event.api.addEventListener('firstDataRendered', () => {
      event.api.sizeColumnsToFit();
    });

    event.api.addEventListener('gridSizeChanged', () => {
      event.api.sizeColumnsToFit();
    });
    event.api.addEventListener('modelUpdated', () => {
      this.updateRowCount();
      event.api.sizeColumnsToFit();
    });
  }
  onRowSelected($event: RowSelectedEvent<any>): void {
    if (!$event.node.isSelected()) return;
    this.rowSelected.emit($event);
  }

  private updateRowCount() {
    if (this.gridApi && this.rowCount) {
      this.rowCountDisplay = this.gridApi.getDisplayedRowCount();
    }
  }

  private radioCellRenderer(params: any) {
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'modelSelection';
    radio.className = 'ag-radio-input';
    radio.checked = params.node.isSelected();

    radio.addEventListener('click', () => {
      params.api.forEachNode((node: any) => node.setSelected(false));
      params.node.setSelected(true);
    });

    return radio;
  }

  setRowSelection() {
    if (this.customRowSelection) {
      this.rowSelection = this.customRowSelection;
      return;
    }
    if (this.multiSelect) {
      this.rowSelection = { mode: 'multiRow' };
    } else if (this.rowSelect) {
      this.rowSelection = { mode: 'singleRow' };
    }
    if (this.rowSelection) {
      this.rowSelection.checkboxes = true;
      this.rowSelection.checkboxLocation = 'selectionColumn';
    }
    if (this.useRadioButton && !this.multiSelect) {
      this.rowSelection.checkboxes = false;
    }
  }

  setRadioButton() {
    if (this.useRadioButton && !this.multiSelect) {
      this.colDefs.unshift({
        field: 'selected',
        headerName: '',
        cellRenderer: this.radioCellRenderer.bind(this),
        suppressHeaderContextMenu: true,
        suppressHeaderMenuButton: true,
        sortable: false
      });
    }
  }

  setGridOption(key: ManagedGridOptionKey, rows: any[]) {
    this.gridApi?.setGridOption(key, rows);
  }

  getMainMenuItems: GetMainMenuItems = (params: GetMainMenuItemsParams) => {
    return ['columnFilter', 'columnChooser', 'resetColumns'];
  };

  onSortChanged(event: any) {
    const item = event.columns.find((item: any) => item.sort);
    this.sortBy = item ? `${item.colId} ${item.sort}` : '';
  }

  onFilterChanged(event: any) {
    const filterModel = event.api.getFilterModel();
    this.filterBy = this.constructFilterQuery(filterModel);
  }

  public getQueryParams(): { sortBy: string; filterBy: string } {
    return {
      sortBy: this.sortBy || '',
      filterBy: this.filterBy || ''
    };
  }

  constructFilterQuery(filterModel: any): string {
    let filterQuery = '';
    for (const columnId in filterModel) {
      if (Object.prototype.hasOwnProperty.call(filterModel, columnId)) {
        const filter = filterModel;
        if (filter) {
          if (filter.conditions) {
            filterQuery += this.constructCondition(filter.condition1, columnId);
            filterQuery += ` ${filter.operator} `;
            filterQuery += this.constructCondition(filter.condition2, columnId);
          } else {
            filterQuery += this.constructCondition(filter, columnId);
          }
          filterQuery += ' AND ';
        }
      }
    }
    return filterQuery.endsWith(' AND ') ? filterQuery.slice(0, -5) : filterQuery;
  }

  constructCondition(condition: any, columnId: string): string {
    const operators: Record<string, string> = {
      equals: `= ${condition.filterType == 'number' ? condition.filter : "'" + condition.filter + "'"}`,
      notEqual: `!= ${condition.filterType == 'number' ? condition.filter : "'" + condition.filter + "'"}`,
      greaterThan: `> ${condition.filter}`,
      greaterThanOrEqual: ` >= ${condition.filter}`,
      lessThan: `< ${condition.filter}`,
      lessThanOrEqual: `<= ${condition.filter}`,
      blank: 'IS NULL',
      notBlank: 'IS NOT NULL',
      contains: `LIKE '%${condition.filter}%'`,
      notContains: `NOT LIKE '%${condition.filter}%'`,
      startsWith: `LIKE '${condition.filter}%'`,
      endsWith: `LIKE '%${condition.filter}'`,
      inRange: `BETWEEN '${condition.filter}' AND '${condition.filterTo}'`
    };

    const operator = operators[condition.type];
    return `${columnId} ${operator}`;
  }

  setGridOptions() {
    this.gridOptions.rowModelType = this.rowModelType as RowModelType;

    this.gridOptions.defaultColDef = {
      ...(this.gridOptions.defaultColDef || {}),
      suppressHeaderFilterButton: true,
      filter: true,
      flex: 1,
      minWidth: 10
    };

    // Enable copy-paste functionality
    this.gridOptions.enableCellTextSelection = true;
    this.gridOptions.enableRangeSelection = true;

    this.gridOptions.detailRowHeight = this.detailRowHeight;
    // Configure status bar without row count components (now shown in upper left)
    if (this.rowCount) {
      this.gridOptions.statusBar = {
        statusPanels: [{ statusPanel: 'agAggregationComponent' }]
      };
    } else {
      this.gridOptions.statusBar = undefined;
    }
    if (this.detailCellRendererParams) {
      this.detailCellRendererParams = _.mergeWith(this.defaultNestedGridOptions, this.detailCellRendererParams);
      this.gridOptions.detailCellRendererParams = this.detailCellRendererParams;
    } else {
      this.gridOptions.detailCellRendererParams = this.defaultNestedGridOptions;
    }
    this.gridOptions.getMainMenuItems = this.getMainMenuItems;
    this.gridOptions.onSortChanged = this.onSortChanged.bind(this);
    this.gridOptions.onFilterChanged = this.onFilterChanged.bind(this);
    this.gridOptions.onRowClicked = this.onRowClicked.bind(this);
    this.gridOptions.onRowGroupOpened = (event: any) => {
      if (event.node.expanded) {
        event.node.setSelected(true);
      }
    };
    this.gridOptions.context = this.context ?? this.gridOptions.context;
    this.gridOptions.onCellClicked = event => {
      this.cellClicked.emit(event);
    };
  }
  onSelectionChanged(): void {
    const selectedRows = this.gridApi?.getSelectedRows() || [];
    this.selectedRows.emit(selectedRows);
  }

  setSelected(id: number, isMultiSelect = false) {
    this.gridApi?.forEachNode(node => {
      if (node.data.id === id) {
        node.data.selected = true;
        node.setSelected(true, isMultiSelect);
        node.setData(node.data);
      } else if (!isMultiSelect) {
        node.setSelected(false);
      }
    });
  }

  invokeOnFilterChanged() {
    this.gridApi?.onFilterChanged();
  }

  onRowClicked(event: any) {
    const node = event.node;
    if (node.master) {
      node.setExpanded(!node.expanded);
    }
    node.setSelected(true);
  }
}
